import React, { createContext, useState, useEffect, useContext } from 'react';
import { apiService } from '../config/api';

// Create context
export const SettingsContext = createContext();

// Settings provider component
export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    general: {
      siteName: '',
      tagline: '',
      contactEmail: '',
      phoneNumber: '',
      address: '',
      businessHours: '',
      timezone: ''
    },
    language: {
      defaultLanguage: 'en',
      enablePortuguese: true,
      enableEnglish: true,
      enableArabic: true
    },
    payment: {
      currency: 'USD',
      enablePaypal: false,
      enableStripe: false,
      enableBankTransfer: false,
      paymentMethods: []
    },
    security: {
      enableRateLimiting: false,
      maxRequestsPerMinute: 100,
      enableFirewall: false,
      blockedIPs: []
    }
  });
  const [loading, setLoading] = useState(true);

  // Transform backend array format [{Key, Value}] to object format {key: value}
  const transformSettings = (data) => {
    if (!data) return {};
    
    const transformed = {};
    
    Object.keys(data).forEach(section => {
      if (Array.isArray(data[section])) {
        // Convert array of {Key, Value} objects to regular object
        transformed[section] = {};
        data[section].forEach(item => {
          if (item.Key && item.Value !== undefined) {
            transformed[section][item.Key] = item.Value;
          }
        });
      } else if (typeof data[section] === 'object') {
        // Already in correct format
        transformed[section] = data[section];
      }
    });
    
    return transformed;
  };

  // Deep merge function to properly merge nested objects
  const mergeDeep = (target, source) => {
    const result = { ...target };
    
    Object.keys(source).forEach(key => {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        // If property exists in target and is an object, merge recursively
        if (target[key] && typeof target[key] === 'object' && !Array.isArray(target[key])) {
          result[key] = mergeDeep(target[key], source[key]);
        } else {
          // Otherwise just assign the source value
          result[key] = source[key];
        }
      } else {
        // For non-objects, just assign the value
        result[key] = source[key];
      }
    });
    
    return result;
  };

  useEffect(() => {
    // Fetch the settings from the API
    const fetchSettings = async () => {
      try {
        setLoading(true);
        // Use the public settings endpoint to avoid authentication requirements
        const response = await apiService.get('/settings/public');
        
        if (response) {
          // Transform backend format [{Key, Value}] to object format
          const transformedData = transformSettings(response);
          // Use deep merge for nested objects
          setSettings(prevSettings => mergeDeep(prevSettings, transformedData));
        } else {
          console.warn('No settings data received from server, using placeholders until data is available');
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        // We'll let the UI show the loading state or empty state since we want to rely on the database
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Create a function to refresh settings from the server
  const refreshSettings = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/settings/public');
      
      if (response) {
        // Transform backend format [{Key, Value}] to object format
        const transformedData = transformSettings(response);
        // Use deep merge for nested objects
        setSettings(prevSettings => mergeDeep(prevSettings, transformedData));
      }
    } catch (error) {
      console.error('Error refreshing settings:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SettingsContext.Provider value={{ settings, loading, refreshSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

// Custom hook to use settings
export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
